import React from 'react'
import { Link } from '@tanstack/react-router'
import { ApiMeaningObject } from 'lib/interfaces'
import { SentenceUnit } from 'lib/translation-view-model'
import { Translit } from 'lib/transliteration'
import { thaiRoute } from 'lib/dictionary-routes'

type Props = {
   /** One unit on desktop (the active sentence); all units stacked on mobile. */
   units: SentenceUnit[]
   unitNoun: 'sentence' | 'point'
   translit: Translit
}

const meaningHtml = (meaning: ApiMeaningObject, translit: Translit): string =>
   // Guesses are our own generated ASCII, not dictionary text — mark
   // them instead of passing them through meaning transliteration
   meaning.guessed ? `<i>maybe:</i> ${meaning.meaning}` : translit.meaning(meaning.meaning)

const WordBreakdownCard = (props: Props) => {
   const rowClasses =
      'flex flex-wrap items-center gap-x-[18px] gap-y-2 rounded-xl border border-solid border-transparent px-4 py-3 odd:bg-listOdd'

   return (
      <div className="flex flex-col gap-4">
         {props.units.map((unit, unitIndex) => (
            <div
               key={unit.index}
               className="border border-solid border-borderSubtle rounded-2xl shadow-card overflow-hidden bg-white"
            >
               {unitIndex === 0 && (
                  <div className="text-[12.5px] font-semibold uppercase tracking-[1.1px] text-textMuted select-none pt-4 px-[22px]">
                     Word breakdown
                  </div>
               )}
               <div
                  className={`flex flex-col px-3.5 pb-2.5 ${unitIndex === 0 ? 'pt-2' : 'pt-2.5'}`}
               >
                  {unit.failed ? (
                     <div className="px-4 py-3 text-[15.5px] italic text-textMuted">
                        This {props.unitNoun} couldn&rsquo;t be translated.
                     </div>
                  ) : (
                     unit.words.map((word, index) => {
                        // A split separable verb (ลอง … ดู) renders as one combined row
                        // on its first member; the remaining members are skipped.
                        if (word.hideInBreakdown) return null
                        const displayWord = word.groupDisplay ?? word.word
                        const phonetic = word.groupPhonetic ?? word.phonetic

                        const wordColumn = (
                           <span className="flex-[0_0_160px]">
                              {word.isThai && (
                                 <>
                                    <span className="block thai text-[22px]! leading-[1.35]">
                                       {displayWord}
                                    </span>
                                    {props.translit.enabled && (
                                       <span className="block tlit text-[15px]! mt-[3px]">
                                          {props.translit.of(phonetic)}
                                       </span>
                                    )}
                                 </>
                              )}
                           </span>
                        )
                        const meaningsColumn = (
                           <span className="flex flex-col gap-1 flex-[1_1_240px] min-w-0">
                              {word.meanings.length ? (
                                 word.meanings.map((meaning, mIndex) => (
                                    <span
                                       key={mIndex}
                                       className="text-[15.5px] leading-[1.5] text-bodyGray"
                                    >
                                       <span className="text-accentBlue mr-[7px]">›</span>
                                       <span
                                          dangerouslySetInnerHTML={{
                                             __html: meaningHtml(meaning, props.translit),
                                          }}
                                       />
                                    </span>
                                 ))
                              ) : (
                                 <span className="text-[15.5px] leading-[1.5] text-bodyGray font-bold">
                                    {displayWord}
                                 </span>
                              )}
                           </span>
                        )

                        // Thai words link through to their dictionary entry; other
                        // tokens (numbers, embedded English) are plain rows.
                        return word.isThai ? (
                           <Link
                              {...thaiRoute(word.groupWord ?? word.word)}
                              key={index}
                              className={`${rowClasses} cursor-pointer transition-[border-color,box-shadow] hover:border-accentBlue hover:shadow-row-hover`}
                           >
                              {wordColumn}
                              {meaningsColumn}
                           </Link>
                        ) : (
                           <span key={index} className={rowClasses}>
                              {wordColumn}
                              {meaningsColumn}
                           </span>
                        )
                     })
                  )}
               </div>
               {unitIndex === props.units.length - 1 && (
                  <div className="px-5 pt-2.5 pb-3 border-t border-solid border-footerBorder">
                     <span className="text-[13.5px] italic text-textMuted">
                        Curious about a word? Tap it for the full dictionary entry.
                     </span>
                  </div>
               )}
            </div>
         ))}
      </div>
   )
}

export default WordBreakdownCard
